import React, {useCallback, useEffect, useRef, useState} from 'react';
import {ScanType} from '../contract';
import './CompareFilters.scss';

const ALL_VALUE = '';

const SCAN_TYPE_DISPLAY: Record<string, string> = {
    [ScanType.WIFI_SCAN]: 'Wifi Scan',
    [ScanType.VISUAL_SCAN]: 'Visual Scan'
};

interface FilterSelectProps {
    label: string;
    options: string[];
    value: string | string[];
    multi?: boolean;
    displayFn?: (val: string) => string;
    onChange: (value: any) => void;
}

const FilterSelect = ({label, options, value, multi, displayFn, onChange}: FilterSelectProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectOption = useCallback(
        (optionValue: string) => {
            if (!multi) {
                onChange(optionValue);
                setIsOpen(false);

                return;
            }

            if (optionValue === ALL_VALUE) {
                onChange([]);
                setIsOpen(false);

                return;
            }

            const current = value as string[];
            const next = current.includes(optionValue)
                ? current.filter((v) => v !== optionValue)
                : [...current, optionValue];

            onChange(next);
        },
        [multi, value, onChange]
    );

    const formatLabel = (val: string): string => (displayFn ? displayFn(val) : val);

    const getDisplayText = (): string => {
        if (!multi) {
            return value ? formatLabel(value as string) : 'All';
        }

        const arr = value as string[];

        return arr.length ? arr.map(formatLabel).join(', ') : 'All';
    };

    const isSelected = (optionValue: string): boolean => {
        if (!multi) {
            return optionValue === ALL_VALUE ? !value : value === optionValue;
        }

        const arr = value as string[];

        return optionValue === ALL_VALUE ? arr.length === 0 : arr.includes(optionValue);
    };

    return (
        <div ref={containerRef} className='compare-filter-select'>
            <span className='compare-filter-label'>{label}</span>
            <div className='compare-filter-trigger' onClick={() => setIsOpen(!isOpen)}>
                <span className='compare-filter-value'>{getDisplayText()}</span>
                <span className={`compare-filter-arrow${isOpen ? ' open' : ''}`}>▾</span>
            </div>
            {isOpen && (
                <div className='compare-filter-dropdown'>
                    <div
                        className={`compare-filter-option${isSelected(ALL_VALUE) ? ' selected' : ''}`}
                        onMouseDown={() => selectOption(ALL_VALUE)}>
                        All
                    </div>
                    {options.map((opt) => (
                        <div
                            key={opt}
                            className={`compare-filter-option${isSelected(opt) ? ' selected' : ''}`}
                            onMouseDown={() => selectOption(opt)}>
                            {formatLabel(opt)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export interface CompareFiltersProps {
    scanTypeOptions: string[];
    floorOptions: string[];
    roomOptions: string[];
    scanTypeFilter: string;
    floorFilter: string[];
    roomFilter: string[];
    onScanTypeChange: (value: string) => void;
    onFloorChange: (values: string[]) => void;
    onRoomChange: (values: string[]) => void;
}

export const CompareFilters = ({
    scanTypeOptions,
    floorOptions,
    roomOptions,
    scanTypeFilter,
    floorFilter,
    roomFilter,
    onScanTypeChange,
    onFloorChange,
    onRoomChange
}: CompareFiltersProps): JSX.Element => {
    return (
        <div className='compare-filters'>
            <FilterSelect
                label='Scan Type'
                options={scanTypeOptions}
                value={scanTypeFilter}
                displayFn={(val) => SCAN_TYPE_DISPLAY[val] || val}
                onChange={onScanTypeChange}
            />
            <FilterSelect label='Floor' options={floorOptions} value={floorFilter} multi onChange={onFloorChange} />
            <FilterSelect label='Room' options={roomOptions} value={roomFilter} multi onChange={onRoomChange} />
        </div>
    );
};
